import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import { getAuthUser } from 'store/auth/selectors';
import { getLocale } from 'store/app/selectors';
import { resolveLocale } from 'i18n/locales';
import { normalizeNumberFormatLocale } from 'i18n/arabicDigits';

/** Preference key for user's number format locale (e.g. "en-GB", "de-DE"). Undefined = use browser default. */
export const NUMBER_FORMAT_LOCALE = 'numberFormatLocale';

export const getDefaultLocale = (): string => {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-GB';
};

export interface FormatNumberOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
}

/**
 * Format a number for display using a given locale (handles decimals, thousands separators).
 * Use this when you have a locale string (e.g. from useNumberFormat or preference).
 */
export const formatNumberWithLocale = (
  locale: string,
  value: number | null | undefined,
  options: FormatNumberOptions = {}
): string => {
  const formatLocale = normalizeNumberFormatLocale(locale);
  if (value == null || Number.isNaN(value)) {
    const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
    return (0).toLocaleString(formatLocale, {
      minimumFractionDigits,
      maximumFractionDigits,
      ...options,
    });
  }
  return value.toLocaleString(formatLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });
};

/**
 * Parse a string that may use comma or dot as decimal separator into a number.
 * Use when receiving user input that might be locale-formatted (e.g. "3,60" or "3.60").
 */
export const parseLocaleNumber = (
  value: string | number | null | undefined
): number => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const s = String(value)
    .trim()
    .replace(/\s/g, '')
    .replace(',', '.');
  const num = parseFloat(s);
  return Number.isNaN(num) ? 0 : num;
};

/** Round to 2 decimal places for API consistency. */
export const toApiNumber = (value: number): number =>
  Math.round(value * 100) / 100;

/** Round quantity for API (up to 4 decimal places). */
export const toApiQuantity = (
  value: number,
  maxDecimals: number = 4
): number => {
  const factor = 10 ** maxDecimals;
  return Math.round(value * factor) / factor;
};

/**
 * Plain numeric string for clipboard (no thousands separators, dot decimal).
 * Use when copying values so pasted text is e.g. "1234.56" not "1,234.56".
 */
export const getRawNumberForCopy = (
  value: number | null | undefined
): string => {
  if (value == null || Number.isNaN(value)) return '0.00';
  return Number(value).toFixed(2);
};

/**
 * Dot-decimal display for transaction money fields (max 2 d.p., no trailing zeros).
 * Ignores locale so amounts align with editable inputs in the same form.
 */
export const formatTransactionAmountDisplay = (
  value: number | string | null | undefined
): string => {
  if (value == null || value === '' || value === '-') return '-';
  const num =
    typeof value === 'number' ? value : parseLocaleNumber(value);
  if (Number.isNaN(num)) return String(value);
  return String(toApiNumber(num));
};

/**
 * Dot-decimal display for quantity (up to 4 d.p., trailing zeros trimmed).
 */
export const formatTransactionQuantityDisplay = (
  value: number | string | null | undefined,
  maxFractionDigits: number = 4
): string => {
  if (value == null || value === '' || value === '-') return '-';
  const num =
    typeof value === 'number' ? value : parseLocaleNumber(value);
  if (Number.isNaN(num)) return String(value);
  const rounded = toApiQuantity(num, maxFractionDigits);
  const fixed = rounded.toFixed(maxFractionDigits);
  return fixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

/** @deprecated Use formatTransactionAmountDisplay */
export const formatTransactionEditNumericDisplay = formatTransactionAmountDisplay;

/**
 * Value shown in transaction numeric inputs: amounts rounded to max 2 d.p.
 * (no trailing zeros) once committed as a number.
 * While the draft value is still a string (in-progress typing), keep it as typed
 * so intermediates like "0.", "0.0", or "1.05" are not collapsed until blur.
 */
export const formatTransactionNumericInputValue = (
  fieldKey: string,
  value: unknown,
  amountFieldKeys: ReadonlySet<string> = new Set([
    'gross',
    'net',
    'tax',
    'unitPrice',
    'discountAmount',
    'exchangeRate',
  ])
): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '-') return '';
    // Keep the live keystrokes (trailing "." / "0" after decimal, extra digits, etc.).
    return trimmed.replace(/,/g, '.');
  }
  if (fieldKey === 'quantity') {
    const display = formatTransactionQuantityDisplay(value as number | string);
    return display === '-' ? '' : display;
  }
  if (amountFieldKeys.has(fieldKey)) {
    const display = formatTransactionAmountDisplay(value as number | string);
    return display === '-' ? '' : display;
  }
  return String(value);
};

/** Format with parentheses for negatives (accounting style). */
export const formatNumberWithParens = (
  locale: string,
  value: number
): string => {
  const formatLocale = normalizeNumberFormatLocale(locale);
  if (value < 0) {
    return `(${Math.abs(value).toLocaleString(formatLocale)})`;
  }
  return value.toLocaleString(formatLocale);
};

/**
 * Hook that returns locale-aware number formatters using user preference (APP section)
 * with fallback to browser locale. Use in React components for display formatting.
 */
export const useNumberFormat = () => {
  const preferences = usePreferences();
  const currentUser = useSelector(getAuthUser);
  const appLocale = useSelector(getLocale);

  const locale = useMemo(() => {
    if (resolveLocale(appLocale) === 'ar') {
      return 'ar-u-nu-arab';
    }
    const saved = preferences(
      PreferenceActions.GET,
      PreferenceSections.APP,
      { name: NUMBER_FORMAT_LOCALE },
      currentUser?.userUuid ?? ''
    );
    return (typeof saved === 'string' ? saved : undefined) ?? getDefaultLocale();
  }, [appLocale, preferences, currentUser?.userUuid, currentUser?.prefDetails]);

  const formatNumber = useMemo(
    () =>
      (value: number | null | undefined, options?: FormatNumberOptions) =>
        formatNumberWithLocale(locale, value, options),
    [locale]
  );

  const formatWithParens = useMemo(
    () => (value: number) => formatNumberWithParens(locale, value),
    [locale]
  );

  return { formatNumber, formatWithParens, locale };
};
